import React from 'react'
import './style.css'
import CustomTable from '../../components/Table'
import CustomTableHead from '../../components/Table/TableHead'
import CustomTableBody from '../../components/Table/TableBody'
import CustomTableRow from '../../components/Table/TableRow'
import CustomTableData from '../../components/Table/TableData'
import TablePagination from '../../components/Table/Pagination'
import StaffService from '../../api/staff-service'
import CustomTableDataCTA from '../../components/Table/TableDataCTA'
import { StaffDTO } from '../../interfaces/staff'
import profileIcon from '../../assets/icons/profile-icon.svg'
import SearchIcon from '../../assets/icons/search.svg'
import Empty from '../../assets/icons/Empty'
import DisableStaffModal from '../../components/Modal/Staff/DisableStaffModal'
import RemoveStaffModal from '../../components/Modal/Staff/RemoveStaffModal'
import { FilterComponent } from '../../components/Filters/FilterComponent'
import { ViewStaffsSkeletonLoader } from '../../components/Loader/ViewStaffsSkeletonLoader'

const tableHeadData = ['Name of Staff', 'Phone number', 'Email address', 'Status', '']

interface ViewStaffsProps {
  setNumOfStaffs: (num: number) => void
  handleViewSelect: (view: string, queries?: Record<string, string>) => void
  setStaffMemberId: (id: string) => void
  isSpinning: boolean
}

const ViewStaffs = ({
  setNumOfStaffs,
  handleViewSelect,
  setStaffMemberId,
  isSpinning,
}: ViewStaffsProps) => {
  const [statusFilter, setStatusFilter] = React.useState('All')
  const [currentPage, setCurrentPage] = React.useState(1)
  const [searchInput, setSearchInput] = React.useState('')
  const [debouncedSearchInput, setDebouncedSearchInput] = React.useState('')
  const [staff, setStaff] = React.useState<StaffDTO>()
  const [openDisableModal, setOpenDisableModal] = React.useState(false)
  const [openRemoveModal, setOpenRemoveModal] = React.useState(false)
  const [loading, setLoading] = React.useState(false)

  // Update debounced search input
  React.useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearchInput(searchInput), 800)
    return () => clearTimeout(handler)
  }, [searchInput])

  // Fetch staff list
  const { data, isLoading, mutate } = StaffService.useGetStaffList({
    page: currentPage,
    name: debouncedSearchInput,
  })

  // Filter staff results based on status
  const filteredResults = React.useMemo(() => {
    if (!data?.results) return []

    switch (statusFilter) {
      case 'Active':
        return data.results.filter(
          (userData) => userData.accepted === true && userData.isActive === true
        )
      case 'Inactive':
        return data.results.filter(
          (userData) => userData.accepted === true && userData.isActive === false
        )
      case 'Pending':
        return data.results.filter((userData) => userData.accepted === false)
      default:
        return data.results
    }
  }, [data, statusFilter])

  // Update staff count
  React.useEffect(() => {
    if (data?.totalResults) {
      setNumOfStaffs(data.totalResults)
    }
  }, [data?.totalResults, setNumOfStaffs])

  const handleDisableClick = (data: StaffDTO) => {
    setStaff(data)
    setOpenDisableModal(true)
  }
  const getStatusText = (isActive: boolean, accepted: boolean): string => {
    if (!accepted) return 'Pending'
    return isActive ? 'Active' : 'Inactive'
  }

  const getStatusColor = (isActive: boolean, accepted: boolean): string => {
    return !accepted ? 'text-orange-500' : isActive ? 'text-green-500' : 'text-red-500'
  }

  const searchInputRef = React.useRef<HTMLInputElement>(null)

  return (
    <>
      {isSpinning || isLoading ? (
        <>
          <ViewStaffsSkeletonLoader />
        </>
      ) : (
        <div className="mt-12 grid gap-y-10">
          <form
            onSubmit={(e) => {
              e.preventDefault()
            }}
            className="search-filter-bar flex w-full justify-between gap-2.5"
          >
            <div className="relative w-full flex items-center bg-gray-100 rounded-lg px-2 cursor-text">
              <img
                src={SearchIcon}
                alt="Search"
                className="absolute left-3 "
              />
              <input
                ref={searchInputRef}
                placeholder="Search staff"
                className="w-full py-2.5 pl-10 bg-transparent outline-none"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>

            <div className="flex items-center gap-2">
              <p>{statusFilter}</p>
              <FilterComponent
                onChange={(selected: any) => setStatusFilter(selected?.label || 'All')}
                filterOptions={['All', 'Active', 'Inactive', 'Pending']}
                selectedFilter={statusFilter}
              />
            </div>
          </form>

          <CustomTable>
            <CustomTableHead dataList={tableHeadData} />
            {!isLoading && filteredResults?.length === 0 ? (
              <div className="flex flex-col gap-5 justify-center items-center sm:justify-center sm:items-center h-96 w-auto">
                <Empty />
                <p className="text-sm font-semibold">No results found</p>
              </div>
            ) : (
              <CustomTableBody>
                {filteredResults?.map((userData) => {
                  const user = userData.staff
                  const statusText = getStatusText(userData.isActive, userData.accepted)

                  return (
                    <CustomTableRow key={user.id}>
                      <CustomTableData
                        dataLabel="Name of Staff"
                        text={user.name}
                        imageUrl={user.imageUrl || profileIcon}
                      />
                      <CustomTableData
                        dataLabel="Phone Number"
                        text={user?.phone || 'NIL'}
                      />
                      <CustomTableData
                        dataLabel="Email Address"
                        text={user?.email || 'NIL'}
                      />

                      <CustomTableData
                        dataLabel="Status"
                        text={statusText}
                        className={`${getStatusColor(userData.isActive, userData.accepted)} font-semibold`}
                      />

                      <CustomTableDataCTA dataLabel="More" text="...">
                        <p
                          onClick={() => handleDisableClick(userData)}
                          className={`hover:bg-gray-200 p-3 border-t`}
                        >
                          {userData.isActive ? 'Disable staff' : 'Enable staff'}
                        </p>
                        <p
                          onClick={() => {
                            if (userData.accepted) {
                              setStaff(userData)
                              setOpenRemoveModal(true)
                              // handleRemoveStaff (userData)
                            }
                          }}
                          className={`hover:bg-gray-200 p-3 border-t text-red-500 ${!userData.accepted ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          Remove staff
                        </p>
                        <p
                          onClick={() => {
                            setStaffMemberId(userData.id)
                            handleViewSelect('edit', { staffId: userData.id })
                          }}
                          className="hover:bg-gray-200 p-3 border-t"
                        >
                          Edit/view details
                        </p>
                      </CustomTableDataCTA>
                    </CustomTableRow>
                  )
                })}
              </CustomTableBody>
            )}
          </CustomTable>

          {data?.results && data?.results.length > 0 && (
            <TablePagination
              totalPages={data?.totalPages}
              currPage={data?.page}
              setCurrentPage={setCurrentPage}
            />
          )}

          {openDisableModal && (
            <DisableStaffModal
              open={openDisableModal}
              setIsOpen={setOpenDisableModal}
              loading={loading}
              setloading={setLoading}
              staffId={staff?.staff?.id!}
              initialStatus={staff?.isActive}
              reload={mutate}
            />
          )}
          {openRemoveModal && (
            <RemoveStaffModal
              open={openRemoveModal}
              setIsOpen={setOpenRemoveModal}
              loading={loading}
              setloading={setLoading}
              staffId={staff?.id!}
              reload={mutate}
              // handleRemoveStaff={handleRemoveStaff}
            />
          )}
        </div>
      )}
    </>
  )
}

export default ViewStaffs
